import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Buffer } from "buffer";
import * as CryptoJS from "crypto-js";
import { ToastrService } from "ngx-toastr";
import { BehaviorSubject, map, of, tap } from "rxjs";
import { api } from "../../utils/endpoints";
import { AuthPermissionService } from "./auth-permission.service";
import { CommonDataSharingService } from "./common-data-sharing.service";
import { ApiCallsService } from "./network/api-calls.service";
import { TranslationService } from "./translation.service";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private readonly _secretKey: string = "armstracker-web-encrytion";
  private _agencyDetails$ = new BehaviorSubject<any>(null);
  agency = this._agencyDetails$.asObservable();
  agencyDetails: any;

  constructor(
    private _router: Router,
    private _apiService: ApiCallsService,
    private _permissionsService: AuthPermissionService,
    private _toastr: ToastrService,
    private _commonDataService: CommonDataSharingService,
    private _translations: TranslationService
  ) { }

  login(request: any) {
    return new Promise((resolve, reject) => {
      this._apiService.postDataWithoutLoader(api._login, request).subscribe({
        next: (res: any) => {
          if (res) {
            const decryptResponse: any =
              this._commonDataService.JSONFormatter(res); // Decrypt api response
            this._router.navigate([
              "/auth/two-factor-auth/" + decryptResponse.data.userId,
            ]);
          }
        },
        error: (error: any) => {
          console.error(error, "----apiError");
          reject();
        },
        complete: () => { },
      });
    });
  }

  twoFactorAuth(request: any) {
    return new Promise((resolve, reject) => {
      this._apiService
        .postDataWithoutLoader(api._requestOtp, request)
        .subscribe({
          next: (res: any) => {
            const decryptResponse: any =
              this._commonDataService.JSONFormatter(res);
            if (request.otpMedium == "mobile") {
              this._router.navigate(["/auth/verify-otp/" + request.userId], {
                queryParams: {
                  verificationMethod: "mobile",
                  value: decryptResponse.data.mobile,
                },
              });
            } else {
              this._router.navigate(["/auth/verify-otp/" + request.userId], {
                queryParams: {
                  verificationMethod: "email",
                  value: decryptResponse.data.email,
                },
              });
            }
          },
          error: (error: any) => reject(),
        });
    });
  }

  verifyOtp(request: any) {
    return new Promise((resolve, reject) => {
      this._apiService
        .postDataWithoutLoader(api._verifyOtp, request)
        .subscribe({
          next: (res: any) => {
            const decryptResponse: any =
              this._commonDataService.JSONFormatter(res);
            this._saveUserDetails(decryptResponse.data);
            this._navigateToDashboard(decryptResponse.data.Token);
          },
          error: (error: any) => reject(),
        });
    });
  }

  resendOtp(request: any) {
    return new Promise((resolve, reject) => {
      this._apiService
        .postDataWithoutLoader(api._requestOtp, request)
        .subscribe({
          next: (res: any) => {
            const decryptResponse: any =
              this._commonDataService.JSONFormatter(res);
            const verificationMethod: any =
              request.otpMedium == "mobile" ? "mobile" : "email";
            this._toastr.success(
              "OTP has been sent to your " + verificationMethod
            );
            resolve(decryptResponse);
          },
          error: (error: any) => reject(),
        });
    });
  }

  signOut(): void {
    this._apiService.post(api._logout, {}).subscribe({
      next: (res: any) => {
        if (res) {
          const decryptResponse: any =
            this._commonDataService.JSONFormatter(res);
          this._clearLocalStorage();
        }
      },
      error: (error: any) => console.log(error),
    });
  }

  getUserProfile() {
    return this._apiService.get(api._currentProfile);
  }

  saveUserCredentials(loginDetails: any): void {
    const encryptedDetails = CryptoJS.AES.encrypt(
      JSON.stringify(loginDetails),
      this._secretKey
    ).toString();
    localStorage.setItem("sessionDetails", encryptedDetails);
  }

  private _clearSessionDetails(): void {
    localStorage.removeItem("sessionDetails");
  }

  private _navigateToDashboard(token: any) {
    this._saveAuthToken(token);
    this._router.navigate(["/dashboard"]);
  }

  private _saveAuthToken(token: any): void {
    localStorage.setItem("adminToken", Buffer.from(token).toString("base64"));
    this._permissionsService
      .getAuthPermissions()
      .pipe()
      .subscribe((data) => { });
  }

  private _saveUserDetails(res: any): void {
    const userDetails = JSON.stringify(res);
    localStorage.setItem(
      "userDetails",
      Buffer.from(userDetails).toString("base64")
    );
  }

  private _clearLocalStorage(): void {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("userDetails");
    this._router.navigate(["/auth/login"]);
  }

  get isAuthenticated(): boolean {
    const token = localStorage.getItem("adminToken");
    if (token) return true;
    return false;
  }

  get credentials(): any {
    const credentials = localStorage.getItem("sessionDetails");
    if (credentials) {
      const decryptedBytes = CryptoJS.AES.decrypt(credentials, this._secretKey);
      const decryptedMessage = JSON.parse(
        decryptedBytes.toString(CryptoJS.enc.Utf8)
      );
      return decryptedMessage;
    }
    return null;
  }

  authenticatedUserRouting(): boolean {
    const token = localStorage.getItem("adminToken");
    if (token) return true;
    this._clearLocalStorage();
    return false;
  }

  anonymousRouting(): boolean {
    const token = localStorage.getItem("adminToken");
    if (token) {
      this._router.navigate(["/dashboard"]);
      return false;
    }
    return true;
  }

  getUserInfo() {
    const userDetails = localStorage.getItem("userDetails");
    if (userDetails) return JSON.parse(atob(userDetails || ""));
  }

  getAgencyDetails = () =>
    this.agencyDetails
      ? of(this.agencyDetails)
      : this._apiService.post(api._agencyDetails, {}).pipe(
        tap((res) => {
          if (res) {
            const decryptResponse: any =
              this._commonDataService.JSONFormatter(res); // Decrypt API response
            this.agencyDetails = decryptResponse?.data;
            this._agencyDetails$.next(this.agencyDetails);
            this._translations.setLanguage(this.agencyDetails?.Language);
          }
        }),
        map(() => this.agencyDetails) // Ensure the transformed value is returned
      );
}
